import type { JSX } from "react";
import { useState } from "react";
import { Html, useTexture } from "@react-three/drei";
import { useSceneStore } from "@/state/useSceneStore";
import { useHoverDwell } from "@/hooks/useHoverDwell";
import { track } from "@/lib/analytics";
import { PROJECTS } from "@/ui/MonitorContent/Projects/data/projects";
import { pinLayout, CARD_SIZE } from "@/scene/objects/pinLayout";

const BOARD_POSITION: [number, number, number] = [-0.26, 0.4, 0.95];
const BOARD_ROTATION: [number, number, number] = [0, Math.PI / 2, 0];
const BOARD_PAD = 0.06;
const CARD_DEPTH = 0.012;
const DWELL_MS = 400;

export const PinBoard = (): JSX.Element | null => {
  const setActiveSection = useSceneStore((s) => s.setActiveSection);
  const setActiveMonitorRoute = useSceneStore((s) => s.setActiveMonitorRoute);
  const [hoveredSlug, setHoveredSlug] = useState<string | null>(null);

  const { start, clear } = useHoverDwell<string>({
    delay: DWELL_MS,
    onDwell: (slug) => setHoveredSlug(slug),
  });

  const thumbs = useTexture(PROJECTS.map((p) => p.thumbCrt));
  const cells = pinLayout(PROJECTS.length);

  const cols = Math.ceil(Math.sqrt(PROJECTS.length));
  const rows = Math.ceil(PROJECTS.length / cols);
  const boardW = cols * (CARD_SIZE.w + 0.04) + BOARD_PAD;
  const boardH = rows * (CARD_SIZE.h + 0.04) + BOARD_PAD;

  const openProject = (slug: string): void => {
    setActiveSection("monitor");
    setActiveMonitorRoute(`#/projects/${slug}`);
    track("pin_open", { slug });
  };

  return (
    <group position={BOARD_POSITION} rotation={BOARD_ROTATION}>
      <mesh>
        <planeGeometry args={[boardW, boardH]} />
        <meshStandardMaterial color="#7a5a34" roughness={0.95} />
      </mesh>

      {PROJECTS.map((project, i) => {
        const cell = cells[i];
        if (!cell) return null;
        const tex = thumbs[i];
        if (!tex) return null;
        return (
          <group key={project.slug} position={[cell.x, cell.y, CARD_DEPTH]}>
            <mesh
              onPointerOver={(e) => {
                e.stopPropagation();
                start(project.slug);
                document.body.style.cursor = "pointer";
              }}
              onPointerOut={() => {
                clear();
                setHoveredSlug(null);
                document.body.style.cursor = "auto";
              }}
              onClick={(e) => {
                e.stopPropagation();
                openProject(project.slug);
              }}
            >
              <planeGeometry args={[CARD_SIZE.w, CARD_SIZE.h]} />
              <meshStandardMaterial map={tex} />
            </mesh>
            <mesh position={[0, CARD_SIZE.h / 2 - 0.01, 0.01]}>
              <sphereGeometry args={[0.012, 12, 12]} />
              <meshStandardMaterial color="#d23b3b" />
            </mesh>
          </group>
        );
      })}

      {hoveredSlug && (
        <Html position={[0, boardH / 2 + 0.05, 0.05]} zIndexRange={[10, 0]}>
          <div className="pointer-events-none -translate-x-1/2 -translate-y-full rounded-sm border border-phosphor bg-bg1/80 px-2 py-1 font-mono text-xs uppercase tracking-wider text-phosphor">
            [ {PROJECTS.find((p) => p.slug === hoveredSlug)?.title} ]
          </div>
        </Html>
      )}
    </group>
  );
};
